import React, { useState, useEffect } from 'react';
import { 
  QrCode, Settings, Plus, Trash2, Eye, EyeOff, BarChart3, 
  Star, ThumbsUp, ThumbsDown, Download, Search, Filter,
  Users, Scan, MessageSquare, TrendingUp, UserCog, Infinity, Check, X
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';

const MagicQRAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [qrCodes, setQRCodes] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQR, setSelectedQR] = useState(null);
  
  // User permissions state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editLimit, setEditLimit] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'permissions') {
      fetchUsers();
    }
  }, [activeTab, userSearch]);

  const fetchData = async () => {
    try {
      const [statsRes, qrRes, feedbackRes, settingsRes] = await Promise.all([
        api.get('/admin/magic-qr/stats'),
        api.get('/admin/magic-qr/all-qr-codes?limit=100'),
        api.get('/admin/magic-qr/all-feedback?limit=100'),
        api.get('/admin/magic-qr/settings')
      ]);
      setStats(statsRes.data);
      setQRCodes(qrRes.data.qr_codes);
      setFeedback(feedbackRes.data.feedback);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load Magic QR data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await api.get(`/admin/magic-qr/users-with-limits?search=${userSearch}&limit=50`);
      setUsers(res.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const updateUserLimit = async (userId, newLimit) => {
    try {
      await api.put(`/admin/magic-qr/user-limits/${userId}?limit=${newLimit}`);
      toast.success('User limit updated');
      setEditingUserId(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update limit');
    }
  };

  const resetUserLimit = async (userId) => {
    try {
      await api.delete(`/admin/magic-qr/user-limits/${userId}`);
      toast.success('Limit reset to default');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reset limit');
    }
  };

  const toggleQRActive = async (qrId) => {
    try {
      await api.put(`/admin/magic-qr/qr/${qrId}/toggle-active`);
      toast.success('QR status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update QR status');
    }
  };

  const deleteQR = async (qrId) => {
    if (!confirm('Delete this QR code and all its feedback?')) return;
    try {
      await api.delete(`/admin/magic-qr/qr/${qrId}`);
      toast.success('QR code deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete QR code');
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await api.put('/admin/magic-qr/settings', newSettings);
      toast.success('Settings updated');
      setSettings(newSettings);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="magic-qr-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <QrCode className="w-8 h-8 text-primary" />
              ANANT Magic Tool
            </h1>
            <p className="text-muted-foreground">Manage Magic QR codes for review collection</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={QrCode} label="Total QR Codes" value={stats.total_qr_codes} color="primary" />
            <StatCard icon={Scan} label="Total Scans" value={stats.total_scans} color="blue" />
            <StatCard icon={ThumbsUp} label="Positive Reviews" value={stats.positive_feedback} color="green" />
            <StatCard icon={Star} label="Sent to Google" value={stats.sent_to_google} color="yellow" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 mb-6 overflow-x-auto">
          {['overview', 'permissions', 'qr-codes', 'feedback', 'settings'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 capitalize transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'permissions' && <UserCog className="w-4 h-4" />}
              {tab.replace('-', ' ')}
            </button>
          ))}
        </div>

        {/* Permissions Tab - Full Admin Control */}
        {activeTab === 'permissions' && (
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="glass-card p-4 border-2 border-amber-500/30 bg-amber-500/5">
              <h3 className="font-semibold text-amber-500 mb-2 flex items-center gap-2">
                <UserCog className="w-5 h-5" />
                Full Admin Control
              </h3>
              <p className="text-sm text-muted-foreground">
                Assign specific QR limits to any member. Set <strong>0</strong> to block QR creation, 
                <strong>-1</strong> for unlimited, or any specific number. Custom limits override tier-based defaults.
              </p>
            </div>

            {/* Search */}
            <div className="flex gap-4 items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by name, email, username, mobile..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
            </div>

            {/* Users Table */}
            <div className="glass-card overflow-hidden">
              {usersLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary mx-auto"></div>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left p-4">User</th>
                      <th className="text-left p-4">Status</th>
                      <th className="text-left p-4">Current QR</th>
                      <th className="text-left p-4">Limit</th>
                      <th className="text-left p-4">Limit Type</th>
                      <th className="text-left p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} className="border-t border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <p className="font-medium">{user.name}</p>
                          <p className="text-xs text-muted-foreground">@{user.username} • {user.email}</p>
                          {user.mobile && <p className="text-xs text-muted-foreground">{user.mobile}</p>}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            user.role === 'admin' ? 'bg-purple-500/20 text-purple-500' :
                            user.is_activated ? 'bg-green-500/20 text-green-500' : 
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {user.role === 'admin' ? 'Admin' : user.is_activated ? 'Activated' : 'Free'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="font-mono text-lg">{user.qr_count}</span>
                        </td>
                        <td className="p-4">
                          {editingUserId === user.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={editLimit}
                                onChange={(e) => setEditLimit(e.target.value)}
                                className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded"
                                placeholder="-1=∞"
                                min="-1"
                              />
                              <button
                                onClick={() => updateUserLimit(user.id, parseInt(editLimit))}
                                className="p-1 bg-green-500/20 text-green-500 rounded hover:bg-green-500/30"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setEditingUserId(null)}
                                className="p-1 bg-red-500/20 text-red-500 rounded hover:bg-red-500/30"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="font-mono text-lg flex items-center gap-1">
                              {user.effective_limit === -1 || user.effective_limit >= 999999 ? (
                                <><Infinity className="w-5 h-5" /> </>
                              ) : user.effective_limit}
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.has_custom_limit ? 'bg-amber-500/20 text-amber-500' :
                            user.limit_source === 'premium' ? 'bg-purple-500/20 text-purple-500' :
                            user.limit_source === 'activated' ? 'bg-green-500/20 text-green-500' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {user.has_custom_limit ? 'Custom' : 
                             user.limit_source === 'premium' ? 'Premium' :
                             user.limit_source === 'activated' ? 'Activated' : 'Free Default'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingUserId(user.id);
                                setEditLimit(user.effective_limit === 999999 ? '-1' : user.effective_limit.toString());
                              }}
                              className="px-3 py-1 bg-primary/20 text-primary rounded hover:bg-primary/30 text-sm"
                            >
                              Set Limit
                            </button>
                            {user.has_custom_limit && (
                              <button
                                onClick={() => resetUserLimit(user.id)}
                                className="px-3 py-1 bg-white/5 text-muted-foreground rounded hover:bg-white/10 text-sm"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="glass-card p-4">
                <h4 className="font-medium mb-2">Quick Tip: Block User</h4>
                <p className="text-sm text-muted-foreground">
                  Set limit to <strong>0</strong> to completely block a user from creating Magic QR codes.
                </p>
              </div>
              <div className="glass-card p-4">
                <h4 className="font-medium mb-2">Quick Tip: Unlimited Access</h4>
                <p className="text-sm text-muted-foreground">
                  Set limit to <strong>-1</strong> to grant unlimited QR code creation.
                </p>
              </div>
              <div className="glass-card p-4">
                <h4 className="font-medium mb-2">Quick Tip: Reset to Default</h4>
                <p className="text-sm text-muted-foreground">
                  Click <strong>Reset</strong> to remove custom limit and use tier-based default.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4">Review Distribution</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ThumbsUp className="w-5 h-5 text-green-500" />
                    5-Star (Sent to Google)
                  </span>
                  <span className="font-bold text-green-500">{stats.sent_to_google}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-yellow-500" />
                    4-Star (Positive Feedback)
                  </span>
                  <span className="font-bold text-yellow-500">{stats.positive_feedback - stats.sent_to_google}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ThumbsDown className="w-5 h-5 text-red-500" />
                    Below 4-Star (Internal)
                  </span>
                  <span className="font-bold text-red-500">{stats.negative_feedback}</span>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span>Positive Rate</span>
                  <span className="text-2xl font-bold text-primary">{stats.positive_rate}%</span>
                </div>
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {feedback.slice(0, 5).map((fb, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <div className={`p-2 rounded-full ${fb.rating >= 4 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      {fb.rating >= 4 ? <ThumbsUp className="w-4 h-4 text-green-500" /> : <ThumbsDown className="w-4 h-4 text-red-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{fb.business_name}</p>
                      <p className="text-xs text-muted-foreground">{fb.name} • {fb.rating} stars</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* QR Codes Tab */}
        {activeTab === 'qr-codes' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">{qrCodes.length} QR codes</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {qrCodes.map(qr => (
                <div key={qr.id} className="glass-card p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{qr.business_name}</h4>
                      <p className="text-sm text-muted-foreground">{qr.business_location}</p>
                      <p className="text-xs text-muted-foreground">Owner: {qr.owner_name}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${qr.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                      {qr.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="flex justify-center my-4 p-4 bg-white rounded-lg">
                    <QRCode value={`${window.location.origin}/review/${qr.code}`} size={100} />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center mb-4">
                    <div className="p-2 bg-white/5 rounded">
                      <p className="text-lg font-bold">{qr.scan_count}</p>
                      <p className="text-xs text-muted-foreground">Scans</p>
                    </div>
                    <div className="p-2 bg-white/5 rounded">
                      <p className="text-lg font-bold text-green-500">{qr.positive_count}</p>
                      <p className="text-xs text-muted-foreground">Positive</p>
                    </div>
                    <div className="p-2 bg-white/5 rounded">
                      <p className="text-lg font-bold text-red-500">{qr.negative_count}</p>
                      <p className="text-xs text-muted-foreground">Negative</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleQRActive(qr.id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10 text-sm"
                    >
                      {qr.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {qr.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => deleteQR(qr.id)}
                      className="px-3 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="space-y-4">
            <div className="flex gap-4 items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search feedback..."
                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              <select className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
                <option value="">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>

            <div className="glass-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4">Business</th>
                    <th className="text-left p-4">Customer</th>
                    <th className="text-left p-4">Rating</th>
                    <th className="text-left p-4">Category</th>
                    <th className="text-left p-4">Feedback</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {feedback.map((fb, idx) => (
                    <tr key={idx} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <p className="font-medium">{fb.business_name}</p>
                      </td>
                      <td className="p-4">
                        <p>{fb.name}</p>
                        <p className="text-xs text-muted-foreground">{fb.phone}</p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          {[1,2,3,4,5].map(star => (
                            <Star 
                              key={star} 
                              className={`w-4 h-4 ${star <= fb.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} 
                            />
                          ))}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-white/10 rounded text-xs">
                          {fb.issue_category || '-'}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm line-clamp-2 max-w-xs">{fb.feedback_text}</p>
                      </td>
                      <td className="p-4">
                        {fb.sent_to_google ? (
                          <span className="px-2 py-1 bg-green-500/20 text-green-500 rounded-full text-xs">
                            Google
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-xs">
                            Internal
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {new Date(fb.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && settings && (
          <div className="glass-card p-6 max-w-2xl">
            <h3 className="text-lg font-semibold mb-6">QR Code Limits</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Free Users Limit</label>
                <input
                  type="number"
                  value={settings.limits?.free_limit || 1}
                  onChange={(e) => setSettings({
                    ...settings,
                    limits: { ...settings.limits, free_limit: parseInt(e.target.value) }
                  })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Activated Users Limit</label>
                <input
                  type="number"
                  value={settings.limits?.activated_limit || 5}
                  onChange={(e) => setSettings({
                    ...settings,
                    limits: { ...settings.limits, activated_limit: parseInt(e.target.value) }
                  })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Premium/Admin Limit</label>
                <input
                  type="number"
                  value={settings.limits?.premium_limit || 20}
                  onChange={(e) => setSettings({
                    ...settings,
                    limits: { ...settings.limits, premium_limit: parseInt(e.target.value) }
                  })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              
              <button
                onClick={() => updateSettings(settings)}
                className="btn-primary mt-4"
              >
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    primary: 'text-primary bg-primary/20',
    blue: 'text-blue-500 bg-blue-500/20',
    green: 'text-green-500 bg-green-500/20',
    yellow: 'text-yellow-500 bg-yellow-500/20',
    red: 'text-red-500 bg-red-500/20'
  };

  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
};

export default MagicQRAdmin;
